import React, { forwardRef, ReactNode } from 'react';

import {
  getWrapperClasses,
  baseClasses,
  sizeMap,
  LabelSizeMap,
  Variant,
  SizeVariant,
  WeightVariant
} from 'lib/dga-input-components-variants/dga-inputs-variants';

export interface DGAInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string | React.ReactNode;
  labelClassName?: string;
  containerClassName?: string;
  inputWrapperClassName?: string;
  error?: boolean;
  errorMessage?: string | React.ReactNode;
  errorClassName?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  helperText?: string | React.ReactNode;
  helperTextClassName?: string;
  inputClassName?: string;
  requiredIndicator?: ReactNode;
  InputSize?: SizeVariant;
  LabelSize?: SizeVariant;
  LabelWeight?: WeightVariant;
  variant?: Variant;
}

/**
 * DGAInput Component
 *
 * @param {string | React.ReactNode} label - Input label
 * @param {string} labelClassName - Custom class for label
 * @param {string} containerClassName - Custom class for outer container
 * @param {string} inputWrapperClassName - Custom class for input wrapper
 * @param {boolean} error - Show error state
 * @param {boolean | React.ReactNode} errorMessage - Error message
 * @param {string} errorClassName - Custom class for error text
 * @param {ReactNode} startIcon - Icon before input
 * @param {ReactNode} endIcon - Icon after input
 * @param {string | React.ReactNode} helperText - Helper/description text
 * @param {string} helperTextClassName - Custom class for helper text
 * @param {string} inputClassName - Custom class for input element
 * @param {ReactNode} requiredIndicator - Custom indicator for required fields
 * @param {'md' | 'lg'} InputSize - Input size variant
 * @param {'md' | 'lg'} LabelSize - Label size variant
 * @param {'regular' | 'semibold'} LabelWeight - Label font weight
 * @param {'default' | 'Filled-Darker' | 'Filled-Lighter'} variant - Style variant
 */
const DGAInput = forwardRef<HTMLInputElement, DGAInputProps>(({
  label,
  labelClassName = '',
  containerClassName = '',
  inputWrapperClassName = '',
  error,
  errorMessage,
  errorClassName = '',
  startIcon,
  endIcon,
  helperText,
  helperTextClassName = '',
  inputClassName = '',
  requiredIndicator,
  InputSize = 'lg',
  LabelSize = 'md',
  LabelWeight = 'regular',
  variant = 'default',
  ...inputProps
}, ref) => {
  const inputId = inputProps?.id || `input-${inputProps?.name}` || `input-${Math.random().toString(36).substr(2, 9)}`;

  // Adjust label size/weight dynamically
  const size = {
    ...sizeMap[InputSize],
    label: `${LabelSizeMap[LabelSize]} ${LabelSizeMap[LabelWeight]}`
  };

  // Shared wrapper classes
  const wrapperClasses = getWrapperClasses(
    'input',
    variant,
    error,
    inputProps.disabled,
    startIcon,
    endIcon,
    inputWrapperClassName,
    inputProps?.readOnly
  );

  // Shared bases
  const finalInputClasses = `${baseClasses.inputBase(size, inputProps?.readOnly, inputProps?.disabled)} ${inputClassName}`.trim();
  const finalLabelClasses = `${baseClasses.labelBase(inputProps?.disabled, size.label)} ${labelClassName}`.trim();
  const finalErrorClasses = `${baseClasses.errorBase} ${errorClassName}`.trim();
  const finalHelperTextClasses = `${baseClasses.descriptionBase} ${helperTextClassName}`.trim();
  const finalContainerClasses = `${inputProps?.readOnly ? '!pointer-events-none' : ''} ${baseClasses.containerBase} ${containerClassName}`.trim();

  const handleFocusOnInputField = () => {
    const inputElement = document?.getElementById?.(inputId);
    if (inputElement) {
      inputElement.focus();
    }
  };

  return (
    <div className={finalContainerClasses}>
      {label && (
        <label htmlFor={inputProps?.id || inputId} className={finalLabelClasses}>
          {label}
          {inputProps?.required && (requiredIndicator || <span className={`${inputProps.disabled ? 'text-neutral-400' : 'text-[#B42318]'}`}>*</span>)}
        </label>
      )}

      <div onClick={handleFocusOnInputField} className={wrapperClasses}>
        {startIcon && (
          <div className="flex-shrink-0 text-gray-400 flex items-center justify-center">
            {startIcon}
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          name={inputProps?.name}
          required={inputProps?.required}
          className={finalInputClasses}
          {...inputProps}
        />

        {endIcon && (
          <div className="flex-shrink-0 text-gray-400 flex items-center justify-center">
            {endIcon}
          </div>
        )}
      </div>

      {helperText && !error && (
        <p className={finalHelperTextClasses}>{helperText}</p>
      )}

      {error && (
        <p className={finalErrorClasses} role="alert">{errorMessage}</p>
      )}
    </div>
  );
});

DGAInput.displayName = 'DGAInput';

export default React.memo(DGAInput);